// src/components/research/ResearchTool.jsx
// Kind-parametrische Listen-Ansicht des Research-Tools: rendert entweder Fragen
// (Q&A) oder Zitate. Beide teilen Layout, Sub-Editoren (Tags/Ideen/Querverweise/
// Quellen) und die rechte Quellen-Suche. Querverweise sind heterogen (Frage<->Zitat);
// ein Klick auf einen kind-fremden Verweis wechselt via onJumpEntity den Reiter.

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import RichTextEditor from "../RichTextEditor";
import SourceCard from "./SourceCard";
import EntityDataBlock from "./EntityDataBlock";
import ResearchMindmap from "./ResearchMindmap";
import {
  sanitizeHtml,
  MANUAL_SOURCE_TYPES,
  formatQuestionNo,
} from "../../services/azureResearchService";
import { extractSqlTables } from "../../services/research/sqlTables";

const EMPTY = [];

// Beschriftungen je Eintragstyp. Inhalts-Slots: plain = Question-Spalte
// (Fragetext / Kontext-Notiz), rich = AnswerHtml-Spalte (Antwort / Zitattext).
const KIND_LABELS = {
  question: {
    title: "Research – Q&A",
    plainPlaceholder: "Frage eingeben…",
    plainEmpty: "(Frage ohne Titel)",
    richEmpty: "Noch keine Antwort.",
    addLabel: "Frage hinzufügen",
    listSearch: "Fragen durchsuchen (Frage & Tags)…",
    emptyList: "Noch keine Fragen.",
    emptyAdd: "Lege unten die erste Frage an.",
    loadingLabel: "Q&A wird geladen…",
    badge: "Frage",
  },
  quote: {
    title: "Research – Zitate",
    plainPlaceholder: "Kontext / Fundstelle (optional)…",
    plainEmpty: "(kein Kontext)",
    richEmpty: "Noch kein Zitat.",
    addLabel: "Zitat hinzufügen",
    listSearch: "Zitate durchsuchen (Text & Tags)…",
    emptyList: "Noch keine Zitate.",
    emptyAdd: "Lege unten das erste Zitat an.",
    loadingLabel: "Zitate werden geladen…",
    badge: "Zitat",
  },
};

const kindBadge = (kind) => KIND_LABELS[kind]?.badge || "Frage";

// Wandelt http(s)-URLs im freien Text in anklickbare Links um (Rest bleibt Plain-Text).
const URL_SPLIT_RE = /(https?:\/\/[^\s)]+)/g;
const linkify = (text) => {
  const str = String(text || "");
  return str.split(URL_SPLIT_RE).map((part, i) =>
    /^https?:\/\//i.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      part
    )
  );
};

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Kurzes Anzeige-Label eines Eintrags (für Querverweis-Chips/-Treffer).
const entityLabel = (e) => {
  if (!e) return "(gelöscht)";
  if (e.kind === "quote") {
    return e.question?.trim() || stripHtml(e.answerHtml).slice(0, 80) || "(Zitat ohne Text)";
  }
  return e.question?.trim() || "(Frage ohne Titel)";
};

// Durchsuchbarer Text eines Eintrags (Plain-Slot + Tags + entschärfter Rich-Text
// + SQL-Query). Der SQL-Text enthält die referenzierten Tabellennamen — damit
// lassen sich Einträge über ihre Datenquellen finden.
const entityHaystack = (e) =>
  `${e.question || ""} ${(e.tags || []).join(" ")} ${stripHtml(e.answerHtml)} ${e.sqlQuery || ""}`.toLowerCase();

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// --- Tag-Editor (mit Vorschlägen aus bereits verwendeten Tags) --------------
const TagEditor = ({ tags, suggestions, onAdd, onRemove }) => {
  const [input, setInput] = useState("");
  const sugg = useMemo(() => {
    const t = input.trim().toLowerCase();
    if (!t) return [];
    return suggestions
      .filter(
        (s) =>
          s.toLowerCase().includes(t) && !tags.some((x) => x.toLowerCase() === s.toLowerCase())
      )
      .slice(0, 6);
  }, [input, suggestions, tags]);

  const commit = (val) => {
    const v = (val ?? input).trim();
    if (v) onAdd(v);
    setInput("");
  };

  return (
    <div className="qna-tags">
      <div className="qna-tags-badges">
        {tags.map((t) => (
          <span className="qna-tag" key={t}>
            {t}
            <button type="button" title="Tag entfernen" onClick={() => onRemove(t)}>
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="qna-tags-input-wrap">
        <input
          className="qna-tags-input"
          value={input}
          placeholder="Tag hinzufügen…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            }
          }}
        />
        {sugg.length > 0 && (
          <div className="qna-tags-suggestions">
            {sugg.map((s) => (
              <button
                key={s}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Kapitel-/Unterkapitel-Auswahl (Single-Select, "wie Tags": wählen oder neu
// anlegen). Über `label`/`noun` auch für Unterkapitel nutzbar.
const ChapterSelect = ({ chapters, value, onSelect, onCreate, label = "Kapitel:", noun = "Kapitel" }) => {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const current = chapters.find((c) => c.id === value) || null;
  const q = input.trim().toLowerCase();
  const matches = chapters
    .filter((c) => (q ? c.name.toLowerCase().includes(q) : true))
    .slice(0, 8);
  const canCreate = !!q && !chapters.some((c) => c.name.toLowerCase() === q);

  const createAndClose = () => {
    const n = input.trim();
    if (n) onCreate(n);
    setInput("");
    setOpen(false);
  };

  return (
    <div className="qna-chapter">
      <span className="qna-chapter-label">{label}</span>
      {current && (
        <span className="qna-chapter-current">
          {current.name}
          <button type="button" title={`${noun}zuordnung entfernen`} onClick={() => onSelect("")}>
            ×
          </button>
        </span>
      )}
      <div className="qna-chapter-input-wrap">
        <input
          className="qna-chapter-input"
          value={input}
          placeholder={current ? `${noun} wechseln…` : `${noun} wählen / anlegen…`}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (canCreate) createAndClose();
            }
          }}
        />
        {open && (matches.length > 0 || canCreate) && (
          <div className="qna-chapter-suggestions">
            {matches.map((c) => (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(c.id);
                  setInput("");
                  setOpen(false);
                }}
              >
                {c.name}
              </button>
            ))}
            {canCreate && (
              <button
                type="button"
                className="qna-chapter-create"
                onMouseDown={(e) => e.preventDefault()}
                onClick={createAndClose}
              >
                ＋ „{input.trim()}" anlegen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Kapitel-Verwaltung (anlegen, umbenennen, neu anordnen, löschen) ---------
// Inklusive Unterkapitel je Kapitel (eingerückt, gleiche Bedienung; ADR 0038).

// Eine Zeile (Kapitel ODER Unterkapitel): Auf/Ab, Name-Inline-Edit, Löschen.
const ChapterManagerRow = ({ item, idx, count, onMove, onRename, onDelete, deleteConfirm }) => (
  <div className="chapter-manager-item">
    <div className="chapter-manager-order">
      <button type="button" disabled={idx === 0} onClick={() => onMove(idx, -1)} title="Nach oben">
        ▲
      </button>
      <button
        type="button"
        disabled={idx === count - 1}
        onClick={() => onMove(idx, 1)}
        title="Nach unten"
      >
        ▼
      </button>
    </div>
    <input
      className="chapter-manager-name"
      defaultValue={item.name}
      onBlur={(e) => {
        const v = e.target.value.trim();
        if (v && v !== item.name) onRename(item.id, v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
    />
    <button
      type="button"
      className="chapter-manager-del"
      title="Löschen"
      onClick={() => {
        if (window.confirm(deleteConfirm)) onDelete(item.id);
      }}
    >
      ×
    </button>
  </div>
);

const ChapterManager = ({
  chapters,
  subchaptersByChapter,
  onAdd,
  onAddSub,
  onRename,
  onReorder,
  onDelete,
  onDeleteSub,
  onClose,
}) => {
  const [newName, setNewName] = useState("");
  // Kapitel-ID -> Entwurfstext für "Neues Unterkapitel…".
  const [subDrafts, setSubDrafts] = useState({});
  const move = (idx, dir) => {
    const ids = chapters.map((c) => c.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    onReorder(ids);
  };
  const addNow = () => {
    const n = newName.trim();
    if (!n) return;
    onAdd(n);
    setNewName("");
  };
  const addSubNow = (chapterId) => {
    const n = (subDrafts[chapterId] || "").trim();
    if (!n) return;
    onAddSub(chapterId, n);
    setSubDrafts((prev) => ({ ...prev, [chapterId]: "" }));
  };
  return (
    <div className="research-modal-overlay" onClick={onClose}>
      <div className="research-modal" onClick={(e) => e.stopPropagation()}>
        <div className="research-modal-header">
          <h3>Kapitel verwalten</h3>
          <button type="button" className="research-modal-close" onClick={onClose} aria-label="Schliessen">
            ×
          </button>
        </div>
        <div className="research-modal-body">
          <ul className="chapter-manager-list">
            {chapters.length === 0 && (
              <li className="chapter-manager-empty">Noch keine Kapitel angelegt.</li>
            )}
            {chapters.map((c, idx) => {
              const subs = subchaptersByChapter.get(c.id) || [];
              const moveSub = (subIdx, dir) => {
                const ids = subs.map((s) => s.id);
                const j = subIdx + dir;
                if (j < 0 || j >= ids.length) return;
                [ids[subIdx], ids[j]] = [ids[j], ids[subIdx]];
                onReorder(ids); // setzt sortOrder je Geschwistergruppe
              };
              return (
                <li key={c.id} className="chapter-manager-chapter">
                  <ChapterManagerRow
                    item={c}
                    idx={idx}
                    count={chapters.length}
                    onMove={move}
                    onRename={onRename}
                    onDelete={onDelete}
                    deleteConfirm={`Kapitel „${c.name}" löschen? Fragen darin werden „Nicht zugeordnet", Unterkapitel werden mitgelöscht.`}
                  />
                  <div className="chapter-manager-subs">
                    {subs.map((s, subIdx) => (
                      <ChapterManagerRow
                        key={s.id}
                        item={s}
                        idx={subIdx}
                        count={subs.length}
                        onMove={moveSub}
                        onRename={onRename}
                        onDelete={onDeleteSub}
                        deleteConfirm={`Unterkapitel „${s.name}" löschen? Fragen darin bleiben im Kapitel „${c.name}".`}
                      />
                    ))}
                    <div className="chapter-manager-add chapter-manager-add--sub">
                      <input
                        value={subDrafts[c.id] || ""}
                        placeholder="Neues Unterkapitel…"
                        onChange={(e) =>
                          setSubDrafts((prev) => ({ ...prev, [c.id]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addSubNow(c.id);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => addSubNow(c.id)}
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="chapter-manager-add">
            <input
              value={newName}
              placeholder="Neues Kapitel…"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addNow();
                }
              }}
            />
            <button type="button" className="btn btn-secondary btn-sm" onClick={addNow}>
              Hinzufügen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Manuelle Quelle erfassen (Buch / Zeitschrift / Interview) ---------------
// Schreibt in die Quellen-Tabelle des Projekts; die neue Quelle erscheint danach
// in Quellen-Liste und -Suche und kann Einträgen zugeordnet werden (ADR 0037).
const ManualSourceModal = ({ onCreate, onClose }) => {
  const [type, setType] = useState("buch");
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [thema, setThema] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const authorLabel = type === "interview" ? "Interviewpartner:in" : "Autor:in";

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    const created = await onCreate({ type, title, author, content, thema, url });
    setSaving(false);
    if (created) onClose();
  };

  return (
    <div className="research-modal-overlay" onClick={onClose}>
      <div className="research-modal" onClick={(e) => e.stopPropagation()}>
        <div className="research-modal-header">
          <h3>Quelle erfassen (Buch / Zeitschrift / Interview)</h3>
          <button type="button" className="research-modal-close" onClick={onClose} aria-label="Schliessen">
            ×
          </button>
        </div>
        <div className="research-modal-body">
          <div className="manual-source-form">
            <label>
              <span>Typ</span>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                {MANUAL_SOURCE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Titel *</span>
              <input
                value={title}
                autoFocus
                placeholder={type === "interview" ? "Titel / Thema des Interviews…" : "Titel…"}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label>
              <span>{authorLabel}</span>
              <input value={author} onChange={(e) => setAuthor(e.target.value)} />
            </label>
            <label>
              <span>Angaben</span>
              <textarea
                rows={3}
                value={content}
                placeholder="z.B. Verlag, Jahr, Ausgabe/Nummer, Datum des Interviews…"
                onChange={(e) => setContent(e.target.value)}
              />
            </label>
            <label>
              <span>Thema</span>
              <input value={thema} onChange={(e) => setThema(e.target.value)} />
            </label>
            <label>
              <span>URL (optional)</span>
              <input value={url} placeholder="https://…" onChange={(e) => setUrl(e.target.value)} />
            </label>
          </div>
          <div className="manual-source-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!title.trim() || saving}
              onClick={save}
            >
              {saving ? "Speichern…" : "Quelle speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Ideen-/Fussnoten-Liste (einzeln erfassbar & bearbeitbar) ----------------
// Gleiche Mechanik für beide: `labels` bestimmt nur die Beschriftungen.
const IDEA_LABELS = {
  title: "Ideen",
  addPlaceholder: "Neue Idee…",
  addButton: "Idee hinzufügen",
  editTitle: "Idee bearbeiten",
  deleteTitle: "Idee löschen",
};
const FOOTNOTE_LABELS = {
  title: "Fussnoten",
  addPlaceholder: "Neue Fussnote…",
  addButton: "Fussnote hinzufügen",
  editTitle: "Fussnote bearbeiten",
  deleteTitle: "Fussnote löschen",
};

// `commitAddOnBlur`: angefangenen Text im Hinzufügen-Feld beim Verlassen
// speichern — damit z.B. ein Klick auf "Fertig" die Eingabe nicht verwirft.
const IdeaList = ({ ideas, labels = IDEA_LABELS, commitAddOnBlur = false, onAdd, onEdit, onRemove }) => {
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const commitAdd = () => {
    const v = text.trim();
    if (v) {
      onAdd(v);
      setText("");
    }
  };

  const startEdit = (idea) => {
    setEditingId(idea.id);
    setEditText(idea.text);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };
  const commitEdit = () => {
    const v = editText.trim();
    if (v && editingId) onEdit(editingId, v);
    cancelEdit();
  };

  return (
    <div className="qna-ideas">
      <div className="qna-ideas-title">
        {labels.title} ({ideas.length})
      </div>
      {ideas.length > 0 && (
        <ul className="qna-ideas-list">
          {ideas.map((i) => (
            <li key={i.id} className="qna-idea">
              {editingId === i.id ? (
                <input
                  className="qna-idea-input qna-idea-edit"
                  value={editText}
                  autoFocus
                  onChange={(e) => setEditText(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitEdit();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      cancelEdit();
                    }
                  }}
                />
              ) : (
                <span
                  className="qna-idea-text"
                  title="Doppelklick zum Bearbeiten"
                  onDoubleClick={() => startEdit(i)}
                >
                  {linkify(i.text)}
                </span>
              )}
              <span className="qna-idea-meta">
                {i.createdBy}
                {i.createdAt ? ` · ${formatDateTime(i.createdAt)}` : ""}
              </span>
              {editingId !== i.id && (
                <button
                  type="button"
                  className="qna-idea-edit-btn"
                  title={labels.editTitle}
                  onClick={() => startEdit(i)}
                >
                  ✎
                </button>
              )}
              <button
                type="button"
                className="qna-idea-del"
                title={labels.deleteTitle}
                onClick={() => onRemove(i.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="qna-idea-add">
        <input
          className="qna-idea-input"
          value={text}
          placeholder={labels.addPlaceholder}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitAddOnBlur ? commitAdd : undefined}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitAdd();
            }
          }}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={commitAdd}>
          {labels.addButton}
        </button>
      </div>
    </div>
  );
};

// --- Querverweise auf andere Einträge (Fragen + Zitate), heterogen ----------
const CrossrefEditor = ({ crossrefs, allEntities, currentId, resolveEntity, onAdd, onRemove, onJump }) => {
  const [input, setInput] = useState("");
  const linkedSet = useMemo(() => new Set((crossrefs || []).map((r) => r.id)), [crossrefs]);

  // Treffer: alle anderen Einträge (beide Typen) über Text + Tags, ohne sich
  // selbst und ohne bereits verknüpfte.
  const matches = useMemo(() => {
    const t = input.trim().toLowerCase();
    if (!t) return [];
    const words = t.split(/\s+/).filter(Boolean);
    return allEntities
      .filter((e) => e.id !== currentId && !linkedSet.has(e.id))
      .filter((e) => {
        const hay = entityHaystack(e);
        return words.every((w) => hay.includes(w));
      })
      .slice(0, 8);
  }, [input, allEntities, linkedSet, currentId]);

  return (
    <div className="qna-crossrefs">
      <div className="qna-crossrefs-title">Querverweise ({(crossrefs || []).length})</div>
      {(crossrefs || []).length > 0 && (
        <div className="qna-crossrefs-list">
          {crossrefs.map((r) => {
            const target = resolveEntity(r.id);
            return (
              <span className="qna-crossref" key={`${r.kind}:${r.id}`}>
                <span className={`qna-cross-badge qna-cross-badge--${r.kind}`}>{kindBadge(r.kind)}</span>
                <button
                  type="button"
                  className="qna-crossref-link"
                  title={target ? "Zum Eintrag springen" : "Eintrag nicht gefunden"}
                  disabled={!target}
                  onClick={() => target && onJump({ kind: r.kind, id: r.id })}
                >
                  {target ? entityLabel(target) : "(gelöscht)"}
                </button>
                <button
                  type="button"
                  className="qna-crossref-del"
                  title="Querverweis entfernen"
                  onClick={() => onRemove(r)}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div className="qna-crossref-add">
        <input
          className="qna-crossref-input"
          value={input}
          placeholder="Frage oder Zitat suchen und verknüpfen…"
          onChange={(e) => setInput(e.target.value)}
        />
        {matches.length > 0 && (
          <div className="qna-crossref-suggestions">
            {matches.map((e) => (
              <button
                key={e.id}
                type="button"
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => {
                  onAdd({ kind: e.kind, id: e.id });
                  setInput("");
                }}
              >
                <span className={`qna-cross-badge qna-cross-badge--${e.kind}`}>{kindBadge(e.kind)}</span>
                {entityLabel(e)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ChangelogLine = ({ changeLog }) => {
  if (!changeLog || changeLog.length === 0) return null;
  const last = changeLog[0];
  return (
    <div className="qna-changelog" title="Letzte Änderungen werden für ein künftiges Dashboard gespeichert">
      Zuletzt bearbeitet: {last.by || "unbekannt"}
      {last.at ? ` · ${formatDateTime(last.at)}` : ""}
      {last.fields?.length ? ` (${last.fields.join(", ")})` : ""}
    </div>
  );
};

// --- Eine Eintrags-Zeile (memoisiert) ---------------------------------------
const EntityRow = React.memo(function EntityRow({
  entity,
  kind,
  index,
  depth = 0,
  isActive,
  readOnly,
  showTags,
  linkedSources,
  tagSuggestions,
  crossrefEntities,
  resolveEntity,
  chapters,
  subchapters,
  onSetChapter,
  onCreateChapter,
  onSetSubchapter,
  onCreateSubchapter,
  parentCandidates,
  parentLabel,
  onSetParent,
  onAddSubQuestion,
  dndEnabled,
  dragActive,
  isDragging,
  onDragStartRow,
  onDragEndRow,
  onDropBefore,
  onDropOnto,
  onActivate,
  onDeactivate,
  onPlainChange,
  onRichChange,
  onSaveNow,
  onUnlink,
  onAddTag,
  onRemoveTag,
  onAddCrossref,
  onRemoveCrossref,
  onJumpEntity,
  onAddIdea,
  onEditIdea,
  onRemoveIdea,
  onAddFootnote,
  onEditFootnote,
  onRemoveFootnote,
  onSetSourceNote,
  onSetTable,
  onTableInput,
  onClearTable,
  onSetChart,
  onChartInput,
  onSqlInput,
}) {
  const id = entity.id;
  const labels = KIND_LABELS[kind] || KIND_LABELS.question;
  const tags = entity.tags || [];
  const ideas = entity.ideas || [];
  const footnotes = entity.footnotes || [];
  const crossrefs = entity.crossrefs || [];

  const handlePlainKeyDown = (e) => {
    // Bei Fragen: Enter speichert + klappt zu. Bei Zitaten ist der Plain-Slot
    // eine mehrzeilige Notiz -> Enter = Zeilenumbruch.
    if (kind === "question" && e.key === "Enter") {
      e.preventDefault();
      onDeactivate(id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.target.blur();
    }
  };

  return (
    <div className="qna-node" style={depth ? { marginLeft: `${depth * 22}px` } : undefined}>
      {/* Drop-Zone "davor" (nur während eines Drags sichtbar) */}
      {dndEnabled && dragActive && !isDragging && (
        <div
          className="qna-drop-before"
          onDragOver={(ev) => {
            ev.preventDefault();
            ev.currentTarget.classList.add("over");
          }}
          onDragLeave={(ev) => ev.currentTarget.classList.remove("over")}
          onDrop={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            ev.currentTarget.classList.remove("over");
            onDropBefore(id);
          }}
        />
      )}
      <div
        id={`qna-row-${id}`}
        className={`qna-row${isActive ? " active" : ""}${isDragging ? " dragging" : ""}`}
        draggable={dndEnabled && !isActive}
        onDragStart={
          dndEnabled && !isActive
            ? (ev) => {
                ev.dataTransfer.effectAllowed = "move";
                onDragStartRow(id);
              }
            : undefined
        }
        onDragEnd={dndEnabled ? () => onDragEndRow() : undefined}
        onDragOver={
          dndEnabled && dragActive && !isDragging
            ? (ev) => {
                ev.preventDefault();
                ev.currentTarget.classList.add("drop-onto");
              }
            : undefined
        }
        onDragLeave={
          dndEnabled && dragActive
            ? (ev) => ev.currentTarget.classList.remove("drop-onto")
            : undefined
        }
        onDrop={
          dndEnabled && dragActive && !isDragging
            ? (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                ev.currentTarget.classList.remove("drop-onto");
                onDropOnto(id);
              }
            : undefined
        }
      >
      {/* Plain-Slot (links): Fragetext bzw. Kontext-Notiz */}
      <div className="qna-question">
        {/* Feste, unveränderliche Fragen-ID — dezent oberhalb der Frage.
            Unabhängig von der laufenden Nummer daneben, die sich beim
            Umsortieren ändert. Zitate haben keine ID. */}
        {kind === "question" && formatQuestionNo(entity.questionNo) && (
          <div className="qna-question-id">{formatQuestionNo(entity.questionNo)}</div>
        )}
        <div className="qna-question-main">
          {dndEnabled && !isActive && (
            <span className="qna-drag-handle" title="Ziehen zum Verschieben / Verschachteln">
              ⠿
            </span>
          )}
          <span className="qna-index">{index + 1}.</span>
          {isActive && !readOnly ? (
            kind === "quote" ? (
              <textarea
                className="qna-context-input"
                value={entity.question}
                placeholder={labels.plainPlaceholder}
                rows={2}
                autoFocus
                onChange={(e) => onPlainChange(id, e.target.value)}
                onBlur={() => onSaveNow(id)}
                onKeyDown={handlePlainKeyDown}
              />
            ) : (
              <input
                className="qna-question-input"
                value={entity.question}
                placeholder={labels.plainPlaceholder}
                autoFocus
                onChange={(e) => onPlainChange(id, e.target.value)}
                onBlur={() => onSaveNow(id)}
                onKeyDown={handlePlainKeyDown}
              />
            )
          ) : (
            <div
              className="qna-question-text"
              onDoubleClick={() => !readOnly && onActivate(id)}
              title={readOnly ? "" : "Doppelklick zum Bearbeiten"}
            >
              {entity.question || <span className="qna-placeholder">{labels.plainEmpty}</span>}
            </div>
          )}
          {!readOnly && isActive && (
            <div className="qna-question-tools">
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => onDeactivate(id)}>
                Fertig
              </button>
            </div>
          )}
        </div>

        {/* Struktur (nur Fragen): Kapitel (oberste Ebene) + Folgefrage-Zuordnung */}
        {isActive && !readOnly && kind === "question" && (
          <div className="qna-structure">
            {!entity.parentId && (
              <ChapterSelect
                chapters={chapters}
                value={entity.chapterId}
                onSelect={(cid) => onSetChapter(id, cid)}
                onCreate={async (name) => {
                  const ch = await onCreateChapter(name);
                  if (ch) onSetChapter(id, ch.id);
                }}
              />
            )}
            {!entity.parentId && entity.chapterId && (
              <ChapterSelect
                chapters={subchapters}
                value={entity.subchapterId}
                label="Unterkapitel:"
                noun="Unterkapitel"
                onSelect={(sid) => onSetSubchapter(id, sid)}
                onCreate={async (name) => {
                  const sc = await onCreateSubchapter(entity.chapterId, name);
                  if (sc) onSetSubchapter(id, sc.id);
                }}
              />
            )}
            <label className="qna-parent-select">
              <span className="qna-chapter-label">Folgefrage von:</span>
              <select
                value={entity.parentId || ""}
                onChange={(ev) => onSetParent(id, ev.target.value)}
              >
                <option value="">— eigenständige Frage —</option>
                {(parentCandidates || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            {entity.parentId && (
              <span className="qna-parent-hint">
                Kapitel wird von der Mutterfrage geerbt.
              </span>
            )}
            {/* Folgefrage direkt hier anlegen (statt neue Frage + Mutterfrage zuweisen). */}
            <div className="qna-subq-add">
              <button
                type="button"
                className="btn btn-secondary btn-sm qna-subq-add-btn"
                onClick={() => onAddSubQuestion(id)}
                title="Folgefrage zu dieser Frage anlegen"
              >
                <span className="qna-subq-plus">+</span> Folgefrage
              </button>
            </div>
          </div>
        )}

        {/* Tags */}
        {isActive && !readOnly ? (
          <TagEditor
            tags={tags}
            suggestions={tagSuggestions}
            onAdd={(t) => onAddTag(id, t)}
            onRemove={(t) => onRemoveTag(id, t)}
          />
        ) : (
          showTags &&
          tags.length > 0 && (
            <div className="qna-tags-badges qna-tags-readonly">
              {tags.map((t) => (
                <span className="qna-tag qna-tag--readonly" key={t}>
                  {t}
                </span>
              ))}
            </div>
          )
        )}
      </div>

      {/* Rich-Slot (rechts): Antwort bzw. Zitattext */}
      <div className="qna-answer">
        {isActive && !readOnly ? (
          <>
            <RichTextEditor
              key={id}
              value={entity.answerHtml}
              onChange={(html) => onRichChange(id, html)}
              onBlur={() => onSaveNow(id)}
            />

            <EntityDataBlock
              dataTable={entity.dataTable}
              chart={entity.chart}
              sqlQuery={entity.sqlQuery}
              exportName={entity.question}
              readOnly={false}
              onSetTable={(t) => onSetTable(id, t)}
              onTableInput={(t) => onTableInput(id, t)}
              onCommit={() => onSaveNow(id)}
              onClearTable={() => onClearTable(id)}
              onSetChart={(c) => onSetChart(id, c)}
              onChartInput={(c) => onChartInput(id, c)}
              onSqlInput={(v) => onSqlInput(id, v)}
            />

            <CrossrefEditor
              crossrefs={crossrefs}
              allEntities={crossrefEntities}
              currentId={id}
              resolveEntity={resolveEntity}
              onAdd={(target) => onAddCrossref(id, target)}
              onRemove={(target) => onRemoveCrossref(id, target)}
              onJump={onJumpEntity}
            />

            <IdeaList
              ideas={ideas}
              onAdd={(text) => onAddIdea(id, text)}
              onEdit={(ideaId, text) => onEditIdea(id, ideaId, text)}
              onRemove={(ideaId) => onRemoveIdea(id, ideaId)}
            />

            <IdeaList
              ideas={footnotes}
              labels={FOOTNOTE_LABELS}
              commitAddOnBlur
              onAdd={(text) => onAddFootnote(id, text)}
              onEdit={(fnId, text) => onEditFootnote(id, fnId, text)}
              onRemove={(fnId) => onRemoveFootnote(id, fnId)}
            />

            <div className="qna-linked">
              <div className="qna-linked-title">Verknüpfte Quellen ({linkedSources.length})</div>
              {linkedSources.length === 0 ? (
                <p className="qna-linked-empty">
                  Noch keine Quellen zugeordnet. Rechts in der Quellen-Suche zuordnen.
                </p>
              ) : (
                <div className="qna-linked-list">
                  {linkedSources.map((s) => (
                    <SourceCard
                      key={s.rk}
                      source={s}
                      note={s.note}
                      onNoteChange={(text) => onSetSourceNote(id, s.rk, text)}
                      onRemove={() => onUnlink(id, s.rk)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div
            className="qna-answer-read"
            onDoubleClick={() => !readOnly && onActivate(id)}
            title={readOnly ? "" : "Doppelklick zum Bearbeiten"}
          >
            {entity.answerHtml ? (
              <div
                className="rte-content"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(entity.answerHtml) }}
              />
            ) : (
              <p className="qna-answer-empty">{labels.richEmpty}</p>
            )}
            <EntityDataBlock
              dataTable={entity.dataTable}
              chart={entity.chart}
              sqlQuery={entity.sqlQuery}
              exportName={entity.question}
              readOnly
            />
            {/* Fussnoten sind in der Listenübersicht immer sichtbar (klein). */}
            {footnotes.length > 0 && (
              <div className="qna-footnotes-read">
                {footnotes.map((f, i) => (
                  <div className="qna-footnote-read" key={f.id}>
                    <span className="qna-footnote-nr">{i + 1}</span>
                    {linkify(f.text)}
                  </div>
                ))}
              </div>
            )}
            {showTags && (
              <div className="qna-answer-meta">
                {crossrefs.length > 0 && <span>{crossrefs.length} Querverweis(e)</span>}
                {ideas.length > 0 && <span>{ideas.length} Idee(n)</span>}
                {(entity.linkedSources?.length || 0) > 0 && (
                  <span>{entity.linkedSources.length} verknüpfte Quelle(n)</span>
                )}
              </div>
            )}
          </div>
        )}

        {isActive && <ChangelogLine changeLog={entity.changeLog} />}
      </div>
      </div>
    </div>
  );
});

const ResearchTool = ({
  editor,
  kind = "question",
  items,
  readOnly,
  isFullscreen,
  toggleFullscreen,
  scrollToId,
  onScrolled,
  onJumpEntity,
  projectName,
}) => {
  const {
    allEntities,
    loading,
    error,
    editingId,
    setEditingId,
    searchTerm,
    setSearchTerm,
    searchResults,
    sourcesLoading,
    refreshSources,
    allTags,
    isSaving,
    isDirty,
    lastSavedAt,
    addEntity,
    setPlainField,
    setRichField,
    saveEntityNow,
    saveDirty,
    linkSource,
    unlinkSource,
    setSourceNote,
    addManualSource,
    canCreateSources,
    addTag,
    removeTag,
    addCrossref,
    removeCrossref,
    addIdea,
    editIdea,
    removeIdea,
    addFootnote,
    editFootnote,
    removeFootnote,
    setDataTable,
    setDataTableLocal,
    clearDataTable,
    setChart,
    setChartLocal,
    setSqlQueryLocal,
    resolveSources,
    resolveEntity,
    chapters,
    subchaptersByChapter,
    setQuestionChapter,
    setQuestionSubchapter,
    addChapter,
    addSubchapter,
    renameChapter,
    reorderChapters,
    deleteChapter,
    deleteSubchapter,
    moveQuestion,
    setQuestionParent,
    addSubQuestion,
  } = editor;

  const labels = KIND_LABELS[kind] || KIND_LABELS.question;

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [listFilter, setListFilter] = useState("");
  const [showTags, setShowTags] = useState(true);
  const [showChapters, setShowChapters] = useState(false); // Kapitel-Verwaltung (Modal)
  const [showAddSource, setShowAddSource] = useState(false); // manuelle Quelle (Modal)
  const [dragId, setDragId] = useState(null); // aktuell gezogene Frage (DnD)
  // Ansicht (nur Fragen): "list" | "mindmap" — Toggle wie bei den Pendenzen.
  const [view, setView] = useState("list");

  // Wechsel in die Mindmap: offenen Editor speichern und schliessen (die
  // Mindmap hat keinen Bearbeitungsmodus, nur Struktur + Drag & Drop).
  const handleSetView = useCallback(
    (next) => {
      if (next === "mindmap" && editingId) {
        saveEntityNow(editingId);
        setEditingId(null);
      }
      setView(next);
    },
    [editingId, saveEntityNow, setEditingId]
  );

  // Export: aktuelle Liste (inkl. aufgelöster Quellen + Querverweise) als JSON.
  const handleExport = useCallback(() => {
    const payload = {
      exportedAt: new Date().toISOString(),
      kind,
      count: items.length,
      entries: items.map((e) => ({
        id: e.id,
        kind: e.kind,
        question: e.question,
        answerHtml: e.answerHtml,
        tags: e.tags || [],
        ideas: e.ideas || [],
        footnotes: e.footnotes || [],
        crossrefs: (e.crossrefs || []).map((r) => ({
          kind: r.kind,
          id: r.id,
          label: entityLabel(resolveEntity(r.id)),
        })),
        linkedSources: resolveSources(e.linkedSources).map((s) => ({
          kind: s.kind || (s.type === "file" ? "file" : "table"),
          rk: s.rk,
          title: s.title,
          url: s.url,
          thema: s.thema,
          type: s.type || "",
          author: s.author || "",
          note: s.note || "",
        })),
        sqlQuery: e.sqlQuery || "",
        sqlTables: extractSqlTables(e.sqlQuery),
        changeLog: e.changeLog || [],
        sortOrder: e.sortOrder,
        createdAt: e.createdAt,
        updatedAt: e.updatedAt,
        createdBy: e.createdBy,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `research-${kind}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [items, kind, resolveSources, resolveEntity]);

  // Liste filtern (Text + Tags, Multi-Wort-AND). Original-Index bleibt erhalten.
  const filteredItems = useMemo(() => {
    const indexed = items.map((e, i) => ({ e, i }));
    const t = listFilter.trim().toLowerCase();
    if (!t) return indexed;
    const words = t.split(/\s+/).filter(Boolean);
    return indexed.filter(({ e }) => {
      const hay = entityHaystack(e);
      return words.every((w) => hay.includes(w));
    });
  }, [items, listFilter]);

  const filtering = !!listFilter.trim();

  // Kinder je Parent (leer = oberste Ebene), je Gruppe nach sortOrder sortiert.
  const childrenByParent = useMemo(() => {
    const m = new Map();
    items.forEach((e) => {
      const pid = e.parentId || "";
      if (!m.has(pid)) m.set(pid, []);
      m.get(pid).push(e);
    });
    m.forEach((arr) =>
      arr.sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
    );
    return m;
  }, [items]);

  // Bei aktiver Suche sichtbar: Treffer UND ihre Vorfahren (Kontext). null = alle.
  const visibleSet = useMemo(() => {
    const t = listFilter.trim().toLowerCase();
    if (!t) return null;
    const words = t.split(/\s+/).filter(Boolean);
    const matches = new Set();
    items.forEach((e) => {
      const hay = entityHaystack(e);
      if (words.every((w) => hay.includes(w))) matches.add(e.id);
    });
    const visible = new Set();
    const mark = (node) => {
      const kids = childrenByParent.get(node.id) || [];
      const anyKid = kids.map(mark).filter(Boolean).length > 0;
      const show = matches.has(node.id) || anyKid;
      if (show) visible.add(node.id);
      return show;
    };
    (childrenByParent.get("") || []).forEach(mark);
    return visible;
  }, [items, listFilter, childrenByParent]);

  // Oberste Ebene nach Kapitel gruppieren; Folgefragen erscheinen NICHT hier,
  // sondern eingerückt unter ihrer Mutterfrage. Reihenfolge = Kapitelreihenfolge,
  // danach "Nicht zugeordnet". Unbekannte (gelöschte) Kapitel-IDs gelten als
  // nicht zugeordnet. Innerhalb eines Kapitels: erst Fragen ohne Unterkapitel,
  // dann die Unterkapitel-Gruppen in Unterkapitel-Reihenfolge (ADR 0038).
  const questionGroups = useMemo(() => {
    if (kind !== "question") return null;
    const chapterIds = new Set(chapters.map((c) => c.id));
    const topLevel = childrenByParent.get("") || [];
    const byChapter = new Map();
    topLevel.forEach((e) => {
      const cid = e.chapterId && chapterIds.has(e.chapterId) ? e.chapterId : "";
      if (!byChapter.has(cid)) byChapter.set(cid, []);
      byChapter.get(cid).push(e);
    });
    const buildSubgroups = (chapter, items) => {
      const subs = chapter ? subchaptersByChapter.get(chapter.id) || [] : [];
      const subIds = new Set(subs.map((s) => s.id));
      const bySub = new Map();
      items.forEach((e) => {
        // Unbekannte (gelöschte) Unterkapitel-IDs gelten als "direkt im Kapitel".
        const sid = e.subchapterId && subIds.has(e.subchapterId) ? e.subchapterId : "";
        if (!bySub.has(sid)) bySub.set(sid, []);
        bySub.get(sid).push(e);
      });
      return [
        { subchapter: null, items: bySub.get("") || [] },
        ...subs.map((s) => ({ subchapter: s, items: bySub.get(s.id) || [] })),
      ];
    };
    const groups = chapters.map((c) => {
      const items = byChapter.get(c.id) || [];
      return { chapter: c, items, subgroups: buildSubgroups(c, items) };
    });
    const rest = byChapter.get("") || [];
    groups.push({ chapter: null, items: rest, subgroups: [{ subchapter: null, items: rest }] });
    return groups;
  }, [kind, childrenByParent, chapters, subchaptersByChapter]);

  // Teilbaum in Anzeigereihenfolge (DFS) mit Tiefe abflachen; bei Suche gefiltert.
  const flattenSubtree = (node, depth, out) => {
    if (visibleSet && !visibleSet.has(node.id)) return;
    out.push({ e: node, depth });
    (childrenByParent.get(node.id) || []).forEach((c) => flattenSubtree(c, depth + 1, out));
  };

  // Externer Scroll-Wunsch (vom Wrapper, z.B. nach Tab-Sprung oder Anlegen).
  useEffect(() => {
    if (!scrollToId) return;
    // Der Zieleintrag existiert nur in der Listen-Ansicht -> ggf. umschalten.
    if (view === "mindmap") {
      setView("list");
      return;
    }
    // Ziel sichtbar machen: aktiven Filter zurücksetzen, dann (Re-Render) scrollen.
    if (listFilter) {
      setListFilter("");
      return;
    }
    const el = document.getElementById(`qna-row-${scrollToId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (onScrolled) onScrolled();
  }, [scrollToId, items, listFilter, onScrolled, view]);

  // Neuer Eintrag: Filter zurücksetzen, anlegen, hinscrollen.
  const handleAddEntity = useCallback(async () => {
    setListFilter("");
    const created = await addEntity(kind);
    if (created && onJumpEntity) onJumpEntity({ kind, id: created.id });
  }, [addEntity, kind, onJumpEntity]);

  // Folgefrage zur Frage `parentId` anlegen: offene Änderungen der Mutterfrage
  // sichern, Filter zurücksetzen (die neue, leere Frage passt sonst nicht zur
  // Suche und wäre unsichtbar), anlegen und hinscrollen. `addSubQuestion` setzt
  // den Editor bereits auf den neuen Eintrag.
  const handleAddSubQuestion = useCallback(
    async (parentId, { jump = true } = {}) => {
      await saveEntityNow(parentId);
      setListFilter("");
      const created = await addSubQuestion(parentId);
      // In der Mindmap NICHT springen — der Sprung würde in die Listen-Ansicht wechseln.
      if (created && jump && onJumpEntity) onJumpEntity({ kind: "question", id: created.id });
      return created;
    },
    [addSubQuestion, saveEntityNow, onJumpEntity]
  );

  // Mindmap: Fragetext inline umbenennen (lokal setzen + sofort persistieren).
  const handleRenameQuestion = useCallback(
    async (id, text) => {
      setPlainField(id, text);
      await saveEntityNow(id);
    },
    [setPlainField, saveEntityNow]
  );

  // Mindmap: Folgefrage anlegen, ohne die Ansicht zu verlassen.
  const handleAddSubQuestionInMindmap = useCallback(
    (parentId) => handleAddSubQuestion(parentId, { jump: false }),
    [handleAddSubQuestion]
  );

  // Mindmap: zur vollen Bearbeitung (Antwort, Tags, Quellen) in die Liste wechseln.
  const handleOpenInList = useCallback(
    (id) => {
      setEditingId(id);
      setView("list");
      if (onJumpEntity) onJumpEntity({ kind: "question", id });
    },
    [setEditingId, onJumpEntity]
  );

  const handleActivate = useCallback(
    (id) => {
      if (editingId && editingId !== id) saveEntityNow(editingId);
      setEditingId(id);
    },
    [editingId, saveEntityNow, setEditingId]
  );

  const handleDeactivate = useCallback(
    (id) => {
      saveEntityNow(id);
      setEditingId(null);
    },
    [saveEntityNow, setEditingId]
  );

  // --- Drag & Drop (nur Fragen) ---------------------------------------------
  const handleDragStartRow = useCallback((id) => setDragId(id), []);
  const handleDragEndRow = useCallback(() => setDragId(null), []);
  // Vor eine Zielzeile fallen lassen: gleiche Ebene/Kapitel/Unterkapitel wie das
  // Ziel, davor einsortiert.
  const handleDropBefore = useCallback(
    (targetId) => {
      if (dragId && dragId !== targetId) {
        const target = items.find((q) => q.id === targetId);
        if (target) {
          moveQuestion(
            dragId,
            target.parentId || "",
            target.chapterId || "",
            target.subchapterId || "",
            targetId
          );
        }
      }
      setDragId(null);
    },
    [dragId, items, moveQuestion]
  );
  // Auf eine Zeile fallen lassen: als Folgefrage dieser Zeile (ans Ende der Kinder).
  const handleDropOnto = useCallback(
    (targetId) => {
      if (dragId && dragId !== targetId) moveQuestion(dragId, targetId, "", "", null);
      setDragId(null);
    },
    [dragId, moveQuestion]
  );
  // In eine Kapitel-Gruppe fallen lassen: oberste Ebene, ans Kapitelende (ohne Unterkapitel).
  const handleDropChapter = useCallback(
    (chapterId) => {
      if (dragId) moveQuestion(dragId, "", chapterId, "", null);
      setDragId(null);
    },
    [dragId, moveQuestion]
  );
  // Auf einen Unterkapitel-Kopf fallen lassen: ans Ende dieses Unterkapitels.
  const handleDropSubchapter = useCallback(
    (chapterId, subchapterId) => {
      if (dragId) moveQuestion(dragId, "", chapterId, subchapterId, null);
      setDragId(null);
    },
    [dragId, moveQuestion]
  );

  const dndEnabled = kind === "question" && !readOnly;

  // Eine Zeile rendern (in flacher Liste UND im Kapitel-/Folgefragen-Baum wiederverwendet).
  const renderEntityRow = (entry, displayIndex) => {
    const { e, depth = 0 } = entry;
    const isActiveRow = editingId === e.id;
    // Mögliche Mutterfragen (nur für die aktive Zeile berechnet): ohne sich selbst
    // und ohne die eigenen Nachfahren (Zyklus-Schutz).
    let parentCandidates = EMPTY;
    if (kind === "question" && isActiveRow) {
      const desc = new Set();
      const collect = (pid) =>
        (childrenByParent.get(pid) || []).forEach((c) => {
          desc.add(c.id);
          collect(c.id);
        });
      collect(e.id);
      parentCandidates = items
        .filter((q) => q.id !== e.id && !desc.has(q.id))
        .map((q) => ({ id: q.id, label: entityLabel(q) || "(ohne Titel)" }));
    }
    const parentLabel =
      e.parentId ? entityLabel(items.find((q) => q.id === e.parentId)) || "(ohne Titel)" : "";
    return (
      <EntityRow
        key={e.id}
        entity={e}
        kind={kind}
        index={displayIndex}
        depth={depth}
        isActive={isActiveRow}
        readOnly={readOnly}
        showTags={showTags}
        linkedSources={isActiveRow ? resolveSources(e.linkedSources) : EMPTY}
        tagSuggestions={isActiveRow ? allTags : EMPTY}
        crossrefEntities={isActiveRow ? allEntities : EMPTY}
        resolveEntity={isActiveRow ? resolveEntity : undefined}
        chapters={kind === "question" ? chapters : EMPTY}
        subchapters={
          kind === "question" && isActiveRow && e.chapterId
            ? subchaptersByChapter.get(e.chapterId) || EMPTY
            : EMPTY
        }
        onSetChapter={setQuestionChapter}
        onCreateChapter={addChapter}
        onSetSubchapter={setQuestionSubchapter}
        onCreateSubchapter={addSubchapter}
        parentCandidates={parentCandidates}
        parentLabel={parentLabel}
        onSetParent={setQuestionParent}
        onAddSubQuestion={handleAddSubQuestion}
        dndEnabled={dndEnabled}
        dragActive={!!dragId}
        isDragging={dragId === e.id}
        onDragStartRow={handleDragStartRow}
        onDragEndRow={handleDragEndRow}
        onDropBefore={handleDropBefore}
        onDropOnto={handleDropOnto}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
        onPlainChange={setPlainField}
        onRichChange={setRichField}
        onSaveNow={saveEntityNow}
        onUnlink={unlinkSource}
        onAddTag={addTag}
        onRemoveTag={removeTag}
        onAddCrossref={addCrossref}
        onRemoveCrossref={removeCrossref}
        onJumpEntity={onJumpEntity}
        onAddIdea={addIdea}
        onEditIdea={editIdea}
        onRemoveIdea={removeIdea}
        onAddFootnote={addFootnote}
        onEditFootnote={editFootnote}
        onRemoveFootnote={removeFootnote}
        onSetSourceNote={setSourceNote}
        onSetTable={setDataTable}
        onTableInput={setDataTableLocal}
        onClearTable={clearDataTable}
        onSetChart={setChart}
        onChartInput={setChartLocal}
        onSqlInput={setSqlQueryLocal}
      />
    );
  };

  // Ctrl/Cmd+S → alle offenen Änderungen speichern.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveDirty();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saveDirty]);

  // Beim Verlassen (Unmount/Tab-Wechsel) offene Änderungen sichern.
  const saveDirtyRef = useRef(saveDirty);
  saveDirtyRef.current = saveDirty;
  useEffect(() => () => saveDirtyRef.current(), []);

  const activeEntity = useMemo(
    () => items.find((e) => e.id === editingId) || null,
    [items, editingId]
  );
  const activeLinkedRks = useMemo(
    () => new Set((activeEntity?.linkedSources || []).map((r) => r.rk)),
    [activeEntity]
  );

  const saveStatus = isSaving
    ? "Speichern…"
    : isDirty
    ? "Ungespeicherte Änderungen"
    : lastSavedAt
    ? `Gespeichert ${lastSavedAt.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}`
    : "";

  return (
    <div className="research-layout">
      <div className="research-main">
        <div className="research-scroll">
          <div className="research-header">
            <h2 className="research-title">{labels.title}</h2>
            <div className="research-header-right">
              {!readOnly && <span className="research-save-status">{saveStatus}</span>}
              {kind === "question" && (
                <div className="research-viewtoggle">
                  <button
                    type="button"
                    className={`research-viewbtn${view === "list" ? " is-active" : ""}`}
                    onClick={() => handleSetView("list")}
                  >
                    Liste
                  </button>
                  <button
                    type="button"
                    className={`research-viewbtn${view === "mindmap" ? " is-active" : ""}`}
                    onClick={() => handleSetView("mindmap")}
                  >
                    Mindmap
                  </button>
                </div>
              )}
              {!readOnly && kind === "question" && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowChapters(true)}
                  title="Kapitel anlegen, umbenennen und neu anordnen"
                >
                  Kapitel verwalten
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowTags((v) => !v)}
                title="Tag-Anzeige in der Übersicht ein-/ausblenden"
              >
                {showTags ? "Tags ausblenden" : "Tags einblenden"}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleExport}>
                Export JSON
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setSidebarOpen((v) => !v)}
              >
                {sidebarOpen ? "Quellen ausblenden" : "Quellen einblenden"}
              </button>
              {toggleFullscreen && (
                <button
                  type="button"
                  className="fullscreen-btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? "Vollbild beenden" : "Vollbild"}
                >
                  {isFullscreen ? "✖" : "⛶"}
                </button>
              )}
            </div>
          </div>

          <div className="research-qsearch">
            <input
              className="research-qsearch-input"
              type="search"
              value={listFilter}
              placeholder={labels.listSearch}
              onChange={(e) => setListFilter(e.target.value)}
            />
          </div>

          {error && <div className="error-message">Fehler: {error}</div>}

          {loading ? (
            <div className="loading">{labels.loadingLabel}</div>
          ) : kind === "question" && view === "mindmap" ? (
            <>
              {items.length === 0 && (
                <p className="qna-empty">
                  {labels.emptyList} {readOnly ? "" : labels.emptyAdd}
                </p>
              )}
              {items.length > 0 && filtering && filteredItems.length === 0 && (
                <p className="qna-empty">Kein Eintrag passt zur Suche.</p>
              )}
              {items.length > 0 && (!filtering || filteredItems.length > 0) && (
                <ResearchMindmap
                  rootLabel={projectName || "Research"}
                  totalCount={items.length}
                  questionGroups={questionGroups}
                  childrenByParent={childrenByParent}
                  visibleSet={visibleSet}
                  readOnly={readOnly}
                  moveQuestion={moveQuestion}
                  onRenameQuestion={handleRenameQuestion}
                  onAddSubQuestion={handleAddSubQuestionInMindmap}
                  onOpenInList={handleOpenInList}
                />
              )}
            </>
          ) : (
            <div className="qna-list">
              {items.length === 0 && (
                <p className="qna-empty">
                  {labels.emptyList} {readOnly ? "" : labels.emptyAdd}
                </p>
              )}
              {items.length > 0 && filteredItems.length === 0 && (
                <p className="qna-empty">Kein Eintrag passt zur Suche.</p>
              )}
              {kind === "question" && questionGroups
                ? (() => {
                    let n = 0;
                    return questionGroups.map((g) => {
                      // Je Untergruppe abflachen (DFS mit Folgefragen), fürs
                      // Ausblenden bei aktiver Suche zusätzlich das Gruppentotal.
                      const subFlats = g.subgroups.map((sg) => {
                        const flat = [];
                        sg.items.forEach((node) => flattenSubtree(node, 0, flat));
                        return { ...sg, flat };
                      });
                      const totalFlat = subFlats.reduce((a, sg) => a + sg.flat.length, 0);
                      if (filtering && totalFlat === 0) return null;
                      const chapterKey = g.chapter ? g.chapter.id : "";
                      return (
                        <div
                          className="qna-group"
                          key={g.chapter ? g.chapter.id : "__none"}
                          onDragOver={dragId ? (ev) => ev.preventDefault() : undefined}
                          onDrop={
                            dragId
                              ? (ev) => {
                                  ev.preventDefault();
                                  handleDropChapter(chapterKey);
                                }
                              : undefined
                          }
                        >
                          <div className="qna-group-header">
                            <span className="qna-group-title">
                              {g.chapter ? g.chapter.name : "Nicht zugeordnet"}
                            </span>
                            <span className="qna-group-count">{g.items.length}</span>
                          </div>
                          {totalFlat === 0 && dragId && (
                            <div className="qna-group-dropzone">
                              Hierher ziehen für „{g.chapter ? g.chapter.name : "Nicht zugeordnet"}"
                            </div>
                          )}
                          {subFlats.map((sg) => {
                            // Bei Suche Untergruppen ohne Treffer ausblenden;
                            // ohne Unterkapitel-Kopf nur die Zeilen rendern.
                            if (filtering && sg.flat.length === 0) return null;
                            const rows = sg.flat.map((entry) => {
                              const num = n;
                              n += 1;
                              return renderEntityRow(entry, num);
                            });
                            if (!sg.subchapter) return rows;
                            return (
                              <div className="qna-subgroup" key={sg.subchapter.id}>
                                <div
                                  className="qna-subgroup-header"
                                  onDragOver={dragId ? (ev) => ev.preventDefault() : undefined}
                                  onDrop={
                                    dragId
                                      ? (ev) => {
                                          ev.preventDefault();
                                          ev.stopPropagation();
                                          handleDropSubchapter(chapterKey, sg.subchapter.id);
                                        }
                                      : undefined
                                  }
                                  title={
                                    dragId
                                      ? `Hierher ziehen für Unterkapitel „${sg.subchapter.name}"`
                                      : undefined
                                  }
                                >
                                  <span className="qna-subgroup-title">{sg.subchapter.name}</span>
                                  <span className="qna-group-count">{sg.items.length}</span>
                                </div>
                                {rows}
                              </div>
                            );
                          })}
                        </div>
                      );
                    });
                  })()
                : filteredItems.map((entry, idx) => renderEntityRow(entry, idx))}
            </div>
          )}
        </div>

        {!readOnly && (
          <button
            type="button"
            className="research-fab"
            onClick={handleAddEntity}
            title={labels.addLabel}
            aria-label={labels.addLabel}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {/* Quellen-Suche (rechts, einklappbar) */}
      <aside className={`research-sources${sidebarOpen ? " open" : " closed"}`}>
        <div className="research-sources-inner">
          <div className="research-sources-head">
            <h3 className="research-sources-title">Quellen-Suche</h3>
            {!readOnly && canCreateSources && (
              <button
                type="button"
                className="research-refresh-btn research-addsource-btn"
                onClick={() => setShowAddSource(true)}
                title="Quelle manuell erfassen (Buch, Zeitschrift oder Interview)"
              >
                +
              </button>
            )}
            <button
              type="button"
              className="research-refresh-btn"
              onClick={refreshSources}
              disabled={sourcesLoading}
              title="Quellen neu laden"
            >
              <span className={sourcesLoading ? "research-refresh-spin" : ""}>⟳</span>
            </button>
          </div>
          <input
            className="research-search-input"
            type="search"
            value={searchTerm}
            placeholder="Quellen & Dateien durchsuchen…"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="research-search-hint">
            {activeEntity
              ? `Zuordnen zu: „${entityLabel(activeEntity)}“`
              : "Eintrag bearbeiten, um Quellen zuzuordnen"}
          </div>
          <div className="research-search-results">
            {searchResults.length === 0 ? (
              <p className="research-search-empty">Keine Treffer.</p>
            ) : (
              searchResults.map((s) => (
                <SourceCard
                  key={s.rk}
                  source={s}
                  onAssign={(src) => linkSource(editingId, src)}
                  assignDisabled={readOnly || !editingId}
                  isAssigned={activeLinkedRks.has(s.rk)}
                />
              ))
            )}
          </div>
        </div>
      </aside>

      {showChapters && kind === "question" && (
        <ChapterManager
          chapters={chapters}
          subchaptersByChapter={subchaptersByChapter}
          onAdd={addChapter}
          onAddSub={addSubchapter}
          onRename={renameChapter}
          onReorder={reorderChapters}
          onDelete={deleteChapter}
          onDeleteSub={deleteSubchapter}
          onClose={() => setShowChapters(false)}
        />
      )}

      {showAddSource && (
        <ManualSourceModal
          onCreate={async (data) => {
            const created = await addManualSource(data);
            // Neue Quelle sofort sichtbar machen (aktiver Suchbegriff würde sie sonst ausblenden).
            if (created) setSearchTerm("");
            return created;
          }}
          onClose={() => setShowAddSource(false)}
        />
      )}
    </div>
  );
};

export default ResearchTool;
